// Wording for the KPI heatmap's cells — one sentence per cell, used both as
// the cell's accessible description and as the card a mouse reader sees, so
// the two can never tell different stories.
//
// A cell's colour carries its outcome, and colour alone carries nothing to a
// reader who cannot see it, so the outcome is always the last clause of the
// sentence. The two empty states are deliberately different words: "no
// measurement due" means the cadence has no period closing in that month,
// while "not yet reported" means one closed and nobody logged against it.
// Collapsing them would hide a missing report behind a blank square.

import type { KpiCellState, KpiCellResult, KpiHeatmapCell } from "../../../types/report";
import { formatReportMonth } from "./reportFormat";

/** How each state reads in prose. `met` / `at risk` / `breached` are the
 *  locked vocabulary shared with the report's other KPI surfaces. */
export const CELL_STATE_LABEL: Record<KpiCellState, string> = {
  met: "Met",
  atRisk: "At risk",
  breached: "Breached",
  notDue: "No measurement due",
  notClosed: "Not yet reported",
};

const OUTCOME_ORDER = [
  { rating: "green" as const, word: "met" },
  { rating: "amber" as const, word: "at risk" },
  { rating: "red" as const, word: "breached" },
];

/** "3 results: 1 met, 1 at risk, 1 breached" — the breakdown behind a cell
 *  whose colour can only show the worst of them. Empty when there is nothing
 *  to break down. */
export function summariseCellResults(results: KpiCellResult[]): string {
  if (results.length === 0) return "";
  const parts = OUTCOME_ORDER.flatMap(({ rating, word }) => {
    const count = results.filter((result) => result.rating === rating).length;
    return count === 0 ? [] : [`${count} ${word}`];
  });
  return `${results.length} results: ${parts.join(", ")}`;
}

/** The full sentence for one cell. A single-result cell states the measured
 *  value against the target; a multi-result cell states the breakdown, since
 *  quoting one of several values would misrepresent the month. */
export function describeKpiCell(
  kpiName: string,
  target: string,
  cell: KpiHeatmapCell,
): string {
  const month = formatReportMonth(cell.month);
  const outcome = CELL_STATE_LABEL[cell.state];

  if (cell.results.length === 0) return `${kpiName} · ${month} · ${outcome}`;

  if (cell.results.length > 1) {
    return `${kpiName} · ${month} · ${summariseCellResults(cell.results)} · ${outcome}`;
  }

  const value = cell.results[0].value.trim();
  if (value === "") return `${kpiName} · ${month} · ${outcome}`;
  const measured = target.trim() === "" ? value : `${value} vs ${target}`;
  return `${kpiName} · ${month} · ${measured} · ${outcome}`;
}
